import { registerModuleUi } from '@b2b/platform-ui/module-ui-registry';
import { lazy } from 'react';

/**
 * DEALS-7A-2A-UI-LIST/v1 — the second real module UI registration, same `lazy()` code-split
 * discipline CODESPLIT/v1 requires project-wide (design doc §2.4), one binding per manifest
 * route (`ogre.manifest.ts`'s own `ui.routes`: `deals` -> `DealsListPage`, `deals/:id` ->
 * `DealDetailPage`).
 */
const DealsListPage = lazy(() =>
  import('@b2b/module-deals/ui/DealsListPage').then((mod) => ({ default: mod.DealsListPage })),
);
const DealDetailPage = lazy(() =>
  import('@b2b/module-deals/ui/DealDetailPage').then((mod) => ({ default: mod.DealDetailPage })),
);
// DEALS-7A-2B-1-BOARD-VIEW/v1 — the Kanban board, `ogre.manifest.ts`'s own `deals/board` ->
// `DealsBoardPage` route, registered as a `secondaryViews[]` entry (below) rather than a second
// `panelRoutes[]` entry — see `moduleUiRegistry.ts#ModulePanelSecondaryView`'s own doc comment for
// why. Same `lazy()` code-split discipline as `DealsListPage`/`DealDetailPage` above.
const DealsBoardPage = lazy(() =>
  import('@b2b/module-deals/ui/DealsBoardPage').then((mod) => ({ default: mod.DealsBoardPage })),
);
// DEALS-BOARD-DEFAULT/v1 (#27) — the board is now the primary list (`/modules/deals/deals`); the old
// `/board` address stays alive as a redirect to it, for bookmarks and links made before.
const DealsBoardLegacyRedirect = lazy(() =>
  import('@b2b/module-deals/ui/DealsBoardLegacyRedirect').then((mod) => ({
    default: mod.DealsBoardLegacyRedirect,
  })),
);
// LINKS-1/v1 (#5) — the "Deals (n)" tab in the company and contact side panels: one lazy chunk
// (`DealsPanelTab.tsx`), four named components.
const dealsPanelTab = () => import('@b2b/module-deals/ui/DealsPanelTab');
const CompanyDealsTabLabel = lazy(() =>
  dealsPanelTab().then((mod) => ({ default: mod.CompanyDealsTabLabel })),
);
const CompanyDealsTabContent = lazy(() =>
  dealsPanelTab().then((mod) => ({ default: mod.CompanyDealsTabContent })),
);
const ContactDealsTabLabel = lazy(() =>
  dealsPanelTab().then((mod) => ({ default: mod.ContactDealsTabLabel })),
);
const ContactDealsTabContent = lazy(() =>
  dealsPanelTab().then((mod) => ({ default: mod.ContactDealsTabContent })),
);

// B2B-SL0-PACKAGE/v1 — Short Links placeholder screens (SL-2 replaces them): one lazy chunk each, as
// CODESPLIT/v1 requires.
const ShortLinksListPage = lazy(() =>
  import('@b2b/module-short-links/ui/ShortLinksListPage').then((mod) => ({
    default: mod.ShortLinksListPage,
  })),
);
const ShortLinkDetailPage = lazy(() =>
  import('@b2b/module-short-links/ui/ShortLinkDetailPage').then((mod) => ({
    default: mod.ShortLinkDetailPage,
  })),
);
const CreateLinkPage = lazy(() =>
  import('@b2b/module-short-links/ui/CreateLinkPage').then((mod) => ({
    default: mod.CreateLinkPage,
  })),
);
const ShortLinksStatisticsPage = lazy(() =>
  import('@b2b/module-short-links/ui/StatisticsPage').then((mod) => ({
    default: mod.StatisticsPage,
  })),
);
const ShortLinksSettingsPage = lazy(() =>
  import('@b2b/module-short-links/ui/SettingsPage').then((mod) => ({ default: mod.SettingsPage })),
);

/**
 * This instance's own module-UI composition root — PER-INSTANCE-BUILD/v1
 * (frontend-composition-root design, Unit B), the direct frontend mirror of
 * `../src/index.ts`'s backend `register*.ts` shape (a real, hand-written,
 * unconditional top-level `registerModuleUi()` call per enabled Module whose
 * manifest declares a `ui` field).
 *
 * **STAGING-REMOVE-HELLO-MODULE/v1 removed `hello`'s registration here.**
 * `hello`'s manifest still declares a real `ui` field
 * (`packages/modules/hello/ogre.manifest.ts`), but this instance no longer
 * enables `hello` (`ogre.instance.json`'s `modules[]`, edited by `ogre
 * remove-module smoke hello`) — so the `HelloPage` lazy import and the
 * `registerModuleUi({ moduleId: 'hello', ... })` call that used to live here
 * are removed with it, per `check:module-ui-registry-wiring`'s own
 * expected/actual agreement rule (this instance's manifest and this
 * instance's own `frontend/` must not disagree in either direction). `hello`
 * stays enabled and registered on other instances that still enable it.
 *
 * A future module's registration goes here, the same way `hello`'s did:
 * `lazy(() => import('@b2b/module-<id>/ui/<Page>.js'))` (CODESPLIT/v1's own
 * pattern, design doc §2.4 — required, not optional, so the page lands in
 * its own chunk rather than the entry bundle) plus an unconditional
 * top-level `registerModuleUi({ moduleId, nav, routes })` call, restating
 * that module's own manifest `ui.nav`/`ui.routes` values (never hand-typed
 * against the module's own conventions). No ordering constraint between two
 * modules' registrations — `moduleUiRegistry.ts`'s own
 * throw-on-duplicate-`moduleId` check is what prevents a collision, not
 * source order.
 */
export function registerModulesUi(): void {
  // MODULE-SIDEPANEL-FOUNDATION/v1 — retrofitted from the original flat `routes[]` registration
  // (DEALS-7A-2A-UI-LIST/v1) onto the new panel-shaped `panelRoutes[]`
  // (`moduleUiRegistry.ts#ModulePanelRouteRegistration`): Deals' create/edit screen now renders
  // inside the SAME `SidePanel` Companies/Contacts/Users/Audit-log already use, per the owner's
  // standing decision ("build that side panel mechanism in a way that it is always used, for all
  // upcoming functions and modules"). `key: 'deals'` doubles as the nav-matching key AND the
  // mounted URL segment (`/modules/deals/deals`) — the same role `routes[0].path` used to play;
  // `nav[0].key` ('deals') still equals it, satisfying `registerModuleUi()`'s own enforced
  // nav-key contract, now checked against `panelRoutes[].key` too. `deals/:id` and a literal
  // `deals/new` are no longer separately registered routes at all — `EntityRoute`'s own
  // `mode="create"`/`mode="detail"` nest `new`/`:id` as CHILDREN of the list route
  // (`TenantApp.tsx`'s own panel-route loop), exactly the way every `entityRegistry` entity
  // already does. `DealDetailPage` serves as both `DetailComponent` and `CreateComponent`
  // unchanged — its own `params.id ?? 'new'` fallback (see that file's own top comment) already
  // resolves to `'new'` when mounted at the literal `new` child route (no `:id` param at all
  // there), the same value it used to receive from the flat `deals/:id` route matching the
  // literal segment `new`.
  registerModuleUi({
    moduleId: 'deals',
    nav: [
      {
        key: 'deals',
        labelKey: 'modules.deals.nav',
        section: 'entities',
        group: 'customers',
        icon: 'deals',
      },
    ],
    panelRoutes: [
      {
        key: 'deals',
        // DEALS-BOARD-DEFAULT/v1 (#27, owner 30 Sep 2026): the BOARD is the default view — the
        // primary list, so the nav entry and every link that names no view open it. The table is
        // the secondary view below.
        ListComponent: DealsBoardPage,
        DetailComponent: DealDetailPage,
        // Reuses the ALREADY-SEEDED keys `DealDetailPage.tsx` itself renders as its own in-body
        // `PageHeader` title (`deals.detail.title`/`deals.create.title`) — the same "reuse an
        // already-seeded key rather than inventing a new one" convention
        // `entities/entityRegistry.ts`'s own `detailTitleKey`/`createTitleKey` doc comment
        // establishes, so this retrofit ships no migration.
        detailTitleKey: 'deals.detail.title',
        CreateComponent: DealDetailPage,
        createTitleKey: 'deals.create.title',
        // DEALS-7A-2B-1-BOARD-VIEW/v1 — secondary views mount under the primary list's path and
        // share this SAME registration's `DetailComponent`/`CreateComponent`/title keys
        // (`getModulePanelRoutes()` computes the full path/nests `new`/`:id` under each view
        // identically to the primary one). DEALS-BOARD-DEFAULT/v1: `table` is the explicit table
        // address (`/modules/deals/deals/table`); `board` is the legacy board address, redirected.
        secondaryViews: [
          { path: 'table', ListComponent: DealsListPage },
          { path: 'board', ListComponent: DealsBoardLegacyRedirect },
        ],
      },
    ],
    panelTabs: [
      {
        key: 'deals',
        target: 'company',
        labelKey: 'deals.panelTab.title',
        Label: CompanyDealsTabLabel,
        Content: CompanyDealsTabContent,
      },
      {
        key: 'deals',
        target: 'contact',
        labelKey: 'deals.panelTab.title',
        Label: ContactDealsTabLabel,
        Content: ContactDealsTabContent,
      },
    ],
    // TAG-CONCURRENCY/v1 (F-4) — the tag merge dialog's and toast's Deals line. `countKey` is the
    // `movedCountKey` Deals registers for `deal_tags` on the server (`DEALS_TAG_ASSOCIATION_OWNERS`).
    tagMergeImpact: [{ countKey: 'dealAssociationsMoved', labelKey: 'deals.tags.mergeImpact' }],
    // PIPE-1/v1 — the deal pipeline is configurable on the Settings page "Phasen & Pipeline": this
    // declares the tab and the REST collection the generic stage-list editor talks to.
    stageLists: [
      {
        key: 'deals',
        labelKey: 'admin.pipeline.tab.deals',
        apiPath: '/deal-stages',
        types: ['new', 'in_progress', 'won', 'lost'],
        order: 10,
        // The Deals screens that show stages: the stage list (board columns, filters, panel select),
        // and every deals query (table, board cards, stats), whose rows carry the nested stage.
        // PIPE-3/v1: also the panel's copy of the probability switch (`deal-pipeline-settings`).
        invalidateQueryKeys: [['deal-stages'], ['deals'], ['deal-pipeline-settings']],
        // PIPE-3/v1: stages carry a default probability, and the switch "Wahrscheinlichkeit im Deal
        // änderbar" (per subsidiary, default OFF) lives at this resource.
        probability: { settingsApiPath: '/deal-pipeline-settings' },
      },
    ],
  });
  // B2B-SL0-PACKAGE/v1 — the Short Links shell (`packages/modules/short-links/ogre.manifest.ts`).
  // The link list is record-shaped, so it registers through the panel-shaped `panelRoutes[]` (Development
  // Guidelines section 3). `DetailComponent` and `detailTitleKey` are the SL-1b placeholder (SL-3b replaces
  // the detail); `CreateComponent`/`createTitleKey` are the create panel of B2B-SL-3A/v1. The settings
  // screen is not a record list, so it stays a flat route; its path equals its nav key (the registry
  // mounts a nav entry at `/modules/<moduleId>/<key>`). The statistics page (B2B-SL-4/v1) is a flat route too, with no nav entry.
  registerModuleUi({
    moduleId: 'short-links',
    nav: [
      {
        key: 'short-links',
        labelKey: 'modules.shortLinks.nav',
        section: 'entities',
        icon: 'link',
      },
      {
        key: 'short-links-settings',
        labelKey: 'modules.shortLinks.navSettings',
        section: 'administration',
      },
    ],
    panelRoutes: [
      {
        key: 'short-links',
        ListComponent: ShortLinksListPage,
        DetailComponent: ShortLinkDetailPage,
        detailTitleKey: 'modules.shortLinks.detail.title',
        // B2B-SL-3A/v1 (PRD B5.1, B5.3): `short-links/new`, the create panel (members and admins; the
        // server refuses everybody else and the panel says so).
        CreateComponent: CreateLinkPage,
        createTitleKey: 'modules.shortLinks.create.title',
      },
    ],
    // B2B-SL-4/v1: the workspace statistics page is a flat route without a navigation entry.
    routes: [
      { path: 'short-links-settings', Component: ShortLinksSettingsPage },
      { path: 'short-links-statistics', Component: ShortLinksStatisticsPage },
    ],
  });
}
